import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Bell, BellOff } from "lucide-react";
import { Button } from "./Button";
import {
  disablePushNotifications,
  enablePushNotifications,
  getPushConfig,
  installedAppMode,
} from "../lib/push-notifications";

export function PushNotificationSettings() {
  const [working, setWorking] = useState(false);
  const [message, setMessage] = useState("");
  const config = useQuery({
    queryKey: ["catchcompass_push_config"],
    queryFn: getPushConfig,
    staleTime: 30_000,
  });

  async function enable() {
    setWorking(true);
    setMessage("");
    try {
      await enablePushNotifications();
      await config.refetch();
      setMessage("Session alerts are enabled on this device.");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Could not enable session alerts.");
    } finally {
      setWorking(false);
    }
  }

  async function disable() {
    setWorking(true);
    setMessage("");
    try {
      await disablePushNotifications();
      await config.refetch();
      setMessage("Session alerts are turned off on this device.");
    } finally {
      setWorking(false);
    }
  }

  return (
    <section className="rounded-xl border border-border bg-raised p-5">
      <div className="flex items-start gap-3">
        <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-inset text-accent">
          {config.data?.subscribed ? <Bell /> : <BellOff />}
        </div>
        <div className="min-w-0">
          <h2 className="font-semibold text-primary">Session alerts</h2>
          <p className="mt-1 text-sm leading-6 text-secondary">
            Useful bankside nudges only: meaningful weather changes, a quiet spell after 4 hours with no recorded activity, and the 15-minute warning before an overdue session automatically closes.
          </p>
        </div>
      </div>

      {!installedAppMode() ? (
        <p className="mt-4 rounded-lg border border-border bg-background p-3 text-xs leading-5 text-secondary">
          On iPhone, install CatchCompass on your Home Screen first. Apple only allows web push notifications from an installed web app.
        </p>
      ) : null}

      {config.data?.supported ? (
        <div className="mt-4">
          {config.data.subscribed ? (
            <Button type="button" variant="tertiary" onClick={disable} isLoading={working}>
              <BellOff className="size-4" /> Turn off session alerts
            </Button>
          ) : (
            <Button type="button" onClick={enable} isLoading={working}>
              <Bell className="size-4" /> Enable session alerts
            </Button>
          )}
        </div>
      ) : config.isLoading ? (
        <p className="mt-4 text-xs text-secondary">Checking notification setup…</p>
      ) : (
        <p className="mt-4 text-xs text-secondary">
          Push delivery is not configured on the CatchCompass server yet.
        </p>
      )}

      {message ? <p className="mt-3 text-xs text-secondary" role="status">{message}</p> : null}
    </section>
  );
}
